import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Expand, Minus, MountainSnow, Plus } from "lucide-react";
import type { HeatmapFeatureCollection, LandmarkCheckin, MapFeatureCollection, MapFeatureProperties, RatingSummary } from "@atlas/shared";
import { AtlasMap, type AtlasMapControls } from "../../components/map/AtlasMap";
import { FilterBar, type FilterType } from "../../components/map/FilterBar";
import { MapInspector } from "../../components/map/MapInspector";
import type { FeatureStaticData } from "../../components/map/FeatureDetail";
import { LANDMARK_CATEGORIES } from "../../lib/landmark-icons";
import { useMediaQuery } from "../../hooks/useMediaQuery";

// The Exploration Map as it looks in the app, running on a fixed snapshot of
// the Joe Bloggs demo account (tools/landing-prototypes/README.md has the
// refresh steps): one real walk in the Peak District, Hope to Hathersage
// over Win Hill and Stanage Edge, and what sits around it. Everything marked
// collected lies on that walk, because that is how the product works: nothing
// is collected without a trace that passes it. It is the real AtlasMap, FilterBar and MapInspector; the
// differences are that the camera is locked to one area and nothing talks to
// the API. The check-in photos are Jack's own from that walk; ratings are
// sample data.

interface DemoData {
  bounds: [number, number, number, number];
  features: MapFeatureCollection;
  heatmap: HeatmapFeatureCollection;
  /** Landmark slug -> the account's check-ins there. */
  checkins: Record<string, LandmarkCheckin[]>;
}

const DATA_URL = "/images/landing/map-demo.json";

/** Stable sample rating per feature, so a pin shows the same stars every visit. */
function sampleRating(f: MapFeatureProperties): RatingSummary {
  let h = 0;
  for (let i = 0; i < f.id.length; i++) h = (h * 31 + f.id.charCodeAt(i)) >>> 0;
  const average = Math.round((3.6 + ((h % 1000) / 1000) * 1.3) * 10) / 10;
  const collected = f.status === "collected";
  return { average, count: 4 + (h % 55), mine: collected ? Math.round(average) : null, canRate: collected };
}

function CtrlBtn({ icon, title, onClick }: { icon: React.ReactNode; title: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        width: 38,
        height: 38,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        background: "var(--surface-overlay)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "none",
        borderBottom: "1px solid var(--border-default)",
        color: "var(--text-secondary)",
      }}
    >
      {icon}
    </button>
  );
}

export function LandingMap() {
  const frameRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<AtlasMapControls | null>(null);
  const dataRef = useRef<DemoData | null>(null);
  const [data, setData] = useState<DemoData | null>(null);
  const [near, setNear] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");
  const [selected, setSelected] = useState<MapFeatureProperties | null>(null);
  const stacked = useMediaQuery("(max-width: 860px)");
  dataRef.current = data;

  // MapLibre and its tiles only load once the section is close.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || !("IntersectionObserver" in window)) { setNear(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); }
    }, { rootMargin: "700px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!near) return;
    let cancelled = false;
    fetch(DATA_URL)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: DemoData | null) => { if (d && !cancelled) setData(d); })
      .catch(() => { /* the frame keeps its empty state */ });
    return () => { cancelled = true; };
  }, [near]);

  // AtlasMap binds its click handlers once, at load, so this must not change.
  const handleSelect = useCallback((f: MapFeatureProperties) => {
    setSelected(f);
    const hit = dataRef.current?.features.features.find((x) => x.properties.id === f.id);
    if (hit?.geometry.type === "Point") controlsRef.current?.flyTo(hit.geometry.coordinates as [number, number]);
  }, []);
  const ignoreBounds = useCallback(() => {}, []);

  const staticDataFor = useMemo(() => {
    return (f: MapFeatureProperties): FeatureStaticData => {
      const slug = f.hasCheckin ? (f.type === "landmark" ? f.slug : f.trigSlug) : undefined;
      const checkins = slug ? data?.checkins[slug] ?? [] : [];
      const first = checkins[0];
      return {
        rating: sampleRating(f),
        checkins,
        cover: first
          ? { photoUrl: first.photoUrl, caption: first.caption, isOwn: true, explorer: { username: null, profilePublic: false } }
          : undefined,
      };
    };
  }, [data]);

  return (
    <div className="cl-app" ref={frameRef}>
      <div className="cl-browser-bar" aria-hidden="true"><i /><i /><i /><span>goatlas.co.uk/map</span></div>
      <div className="cl-app-body">
        {/* The app's own navigation, as a picture: it goes nowhere from here. */}
        <div className="cl-app-nav" aria-hidden="true">
          <img src="/images/landing/web-sidebar-top.webp" alt="" width={264} height={690} loading="lazy" />
          <img src="/images/landing/web-sidebar-bottom.webp" alt="" width={264} height={173} loading="lazy" />
        </div>

        <div className="cl-app-map">
          {data && (
            <AtlasMap
              features={data.features}
              heatmap={data.heatmap}
              selectedId={selected?.id ?? null}
              filter={filter}
              landmarkCategories={LANDMARK_CATEGORIES}
              onFeatureSelect={handleSelect}
              onBoundsChange={ignoreBounds}
              controlsRef={controlsRef}
              cluster={false}
              lockedBounds={data.bounds}
            />
          )}

          <div style={{ position: "absolute", top: 16, left: 16, right: 16, display: "flex", alignItems: "center", gap: 10, zIndex: 10, pointerEvents: "none" }}>
            {!stacked && (
              <div className="atlas-glass" style={{ pointerEvents: "auto", display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: "var(--radius-pill)", boxShadow: "var(--shadow-md)" }}>
                <MountainSnow size={16} color="var(--gold-400)" />
                <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-primary)" }}>Explore</span>
              </div>
            )}
            <div style={{ pointerEvents: "auto", minWidth: 0, flex: stacked ? 1 : undefined, position: "relative" }}>
              <FilterBar filter={filter} onChange={setFilter} features={data?.features ?? null} landmarkCategories={LANDMARK_CATEGORIES} />
            </div>
          </div>

          <div style={{ position: "absolute", left: 16, bottom: 16, display: "flex", flexDirection: "column", gap: 7, padding: "11px 13px", borderRadius: "var(--radius-md)", background: "var(--surface-overlay)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid var(--border-default)", boxShadow: "var(--shadow-md)", zIndex: 10 }}>
            <div className="eyebrow" style={{ fontSize: 9, marginBottom: 1 }}>Legend</div>
            {[
              ["var(--accent)", "Collected"],
              ["var(--status-info)", "In progress"],
              ["rgba(86,103,119,0.7)", "Not visited"],
            ].map(([color, label]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ width: 11, height: 11, borderRadius: "50%", background: color }} />
                <span style={{ fontSize: 11.5, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>{label}</span>
              </div>
            ))}
          </div>

          <div style={{ position: "absolute", right: 16, bottom: 16, display: "flex", flexDirection: "column", borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border-default)", boxShadow: "var(--shadow-md)", zIndex: 10 }}>
            <CtrlBtn icon={<Plus size={17} />} title="Zoom in" onClick={() => controlsRef.current?.zoom(1)} />
            <CtrlBtn icon={<Minus size={17} />} title="Zoom out" onClick={() => controlsRef.current?.zoom(-1)} />
            <CtrlBtn icon={<Expand size={17} />} title="Reset view" onClick={() => { setSelected(null); controlsRef.current?.reset(); }} />
          </div>
        </div>

        {/* As in the app: a permanent rail on desktop, a bottom sheet on
            narrow screens that only exists while something is selected. */}
        {(!stacked || selected != null) && <MapInspector
          sheet={stacked}
          selected={selected}
          features={data?.features ?? null}
          filter={filter}
          landmarkCategories={LANDMARK_CATEGORIES}
          onSelect={handleSelect}
          onDeselect={() => setSelected(null)}
          onFocusMap={handleSelect}
          regionName="Hope Valley and Stanage"
          staticDataFor={staticDataFor}
        />}
      </div>
    </div>
  );
}
